import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * El reporte de un error que ocurrió EN EL NAVEGADOR de quien usa el sistema.
 *
 * ── Por qué existe este contrato ─────────────────────────────────────
 * Todo lo que el servidor sabe de una sesión son las peticiones que le
 * llegan. Cuando la app se rompe en el navegador —un `undefined` al pintar,
 * un chunk de JavaScript que no bajó, una pantalla en blanco— el servidor
 * no ve NADA: no hay petición que falle, solo alguien que dice "no me
 * carga" y una IP que dejó de pedir cosas. Ese es exactamente el error que
 * más caro sale, porque el usuario lo vive y nosotros ni nos enteramos.
 *
 * Este endpoint cierra ese hueco: el navegador cuenta lo que le pasó y el
 * servidor lo escribe en SU log, donde ya vive todo lo demás y donde
 * `scripts/logs/capturar.sh` lo archiva junto al resto.
 *
 * ── Por qué NO se guarda en la base ──────────────────────────────────
 * Un error de navegador no es un dato del negocio: no se consulta desde
 * ninguna pantalla, no se relaciona con nada y su valor caduca en cuanto
 * se arregla el bug. Guardarlo en Mongo sería una colección que solo
 * crece, que hay que podar y que en un mal día —un bug en bucle— se llena
 * a miles de documentos por minuto. El log ya resuelve eso: es
 * append-only, tiene retención automática y ya está archivado en disco.
 *
 * ── Todo campo tiene techo, y es a propósito ─────────────────────────
 * Este endpoint es PÚBLICO y sin sesión, como el de contacto: lo tiene que
 * ser, porque el error que más importa es justo el que tumba la app antes
 * de que alguien entre. Un campo sin techo aquí es un canal para escribir
 * lo que sea en nuestros logs —y un log envenenado es un log que ya no se
 * lee—. De ahí los recortes de abajo y el límite por IP del módulo.
 */
/** Un mensaje de error real cabe de sobra; lo que no cabe es una novela. */
export const CLIENT_ERROR_MESSAGE_MAX = 500;
/**
 * Ocho stacks de navegador típicos caben en 4000 caracteres. Más allá,
 * lo que sigue son marcos de React que se repiten y no dicen nada nuevo:
 * la línea que importa es siempre de las primeras.
 */
export const CLIENT_ERROR_STACK_MAX = 4000;
/** La URL de la pantalla donde reventó. Una URL larga de verdad no pasa de 500. */
export const CLIENT_ERROR_PAGE_MAX = 500;
/**
 * De dónde salió el error. No es decoración: cambia por completo qué se
 * hace con él.
 *   · `render`   — un componente reventó al pintar. La persona VE una
 *                  pantalla rota. Es el más grave de los tres.
 *   · `global`   — una excepción que nadie atrapó (`window.onerror`).
 *   · `promesa`  — un `await` que falló sin `catch` (`unhandledrejection`).
 *                  Suele ser ruidoso: extensiones del navegador y scripts
 *                  de terceros caen aquí.
 */
export const clientErrorSourceSchema = z.enum(["render", "global", "promesa"]);
export const clientErrorReportSchema = z.object({
    source: clientErrorSourceSchema,
    message: z.string().trim().min(1).max(CLIENT_ERROR_MESSAGE_MAX),
    stack: z.string().trim().max(CLIENT_ERROR_STACK_MAX).optional(),
    /** La ruta donde ocurrió, para reproducirlo sin adivinar. */
    page: z.string().trim().max(CLIENT_ERROR_PAGE_MAX).optional(),
    /**
     * Qué versión del frontend estaba corriendo. Sin esto, un error viejo de
     * una pestaña que alguien dejó abierta tres días parece un bug nuevo.
     */
    build: z.string().trim().max(60).optional(),
});
/**
 * La respuesta dice `logged`, no `ok`, por lo mismo que contacto dice
 * `received`: lo único que el servidor puede prometer es que lo escribió.
 * Al navegador tampoco le sirve saber más — no hay pantalla que lo enseñe.
 */
export const clientErrorReceiptSchema = z.object({
    logged: z.literal(true),
});
export const clientErrorResponseSchema = responseSchema(clientErrorReceiptSchema);
// ── Rutas ────────────────────────────────────────────────
export const CLIENT_ERROR_ROUTES = {
    /** Público y sin sesión. Con rate limit por IP en el servidor. */
    report: "/api/client-errors",
};
//# sourceMappingURL=client-error.js.map